/**
 * GoTrue reports some callback failures in the URL *fragment*, which never reaches the server — so
 * the route falls through to `missing_auth_code` and blames the link for something else entirely.
 * Reading the fragment client-side is the only way to recover the real reason.
 */
export interface CallbackHashError {
  code: string | null;
  description: string | null;
}

/** Percent-decodes once more when the value arrives double-encoded, as `error_description` does. */
function decodeOnceMore(value: string): string {
  if (!/%[0-9a-f]{2}/i.test(value)) return value;
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export function parseCallbackErrorHash(hash: string): CallbackHashError | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!raw) return null;

  const params = new URLSearchParams(raw);
  if (!params.get('error') && !params.get('error_code')) return null;

  const description = params.get('error_description');
  return {
    code: params.get('error_code') ?? params.get('error'),
    description: description ? decodeOnceMore(description) : null,
  };
}

/**
 * User-facing copy. The raw text is developer-facing (it can name a database function), so callers
 * log that separately rather than showing it.
 */
export function callbackErrorMessage(error: CallbackHashError): string {
  switch (error.code) {
    case 'otp_expired':
    case 'access_denied':
      return 'That link has expired or has already been used. Request a new one and try again.';
    case 'unexpected_failure':
      return "We couldn't complete that — something went wrong on our side. Please try again.";
    default:
      return "We couldn't complete that sign-in. Please try again.";
  }
}
